import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
	useClerk,
	useOrganization,
	useOrganizationList,
	useSession,
	useSessionList,
	useUser,
} from "@clerk/clerk-react";
import {
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { clerkErrorMessage } from "./clerk-errors.js";
import { PlanBadge, useWorkingCopyPlan } from "./PlanBadge.js";
import { patchWorkspaceName } from "./onboarding-api.js";
import type { OnboardingSession } from "./onboarding-flow.js";
import { usePlatformSession } from "./PlatformSession.js";
import { SignOutConfirm } from "./SignOutConfirm.js";
import {
	clerkOrgRoleToMemberRole,
	memberRoleToClerkOrgRole,
	roleHint,
	type InviteRole,
	type MemberRole,
	type NotificationPrefs,
	type RiskConfig,
	type SettingsTab,
	type WorkspaceMember,
} from "./settings.js";
import { cn } from "./cn.js";
import {
	readThemePreference,
	resolveTheme,
	type ThemePreference,
} from "./theme.js";
import { ClerkOrgPlans } from "./WorkingCopyAsk.js";

type Props = {
	tab: SettingsTab;
	session: OnboardingSession;
	risk: RiskConfig;
	notifications: NotificationPrefs;
	themePref: ThemePreference;
	onSaveAccount: (patch: {
		firstName: string;
		lastName: string;
		email: string;
	}) => void;
	onSaveWorkspace: (patch: {
		company: string;
		website: string;
	}) => void;
	onSaveRisk: (risk: RiskConfig) => void;
	onSaveNotifications: (prefs: NotificationPrefs) => void;
	onCycleTheme: () => void;
};

export function WorkspaceSettings(props: Props) {
	switch (props.tab) {
		case "account":
			return (
				<AccountPanel
					session={props.session}
					themePref={props.themePref}
					onSave={props.onSaveAccount}
					onCycleTheme={props.onCycleTheme}
				/>
			);
		case "workspace":
			return (
				<WorkspacePanel
					session={props.session}
					risk={props.risk}
					onSave={props.onSaveWorkspace}
					onSaveRisk={props.onSaveRisk}
				/>
			);
		case "members":
			return <MembersPanel />;
		case "notifications":
			return (
				<NotificationsPanel
					prefs={props.notifications}
					onSave={props.onSaveNotifications}
				/>
			);
		case "security":
			return <SecurityPanel risk={props.risk} />;
		case "billing":
			return <BillingPanel />;
		default: {
			const _exhaustive: never = props.tab;
			return _exhaustive;
		}
	}
}

function PanelShell({
	title,
	lede,
	children,
}: {
	title: string;
	lede: string;
	children: ReactNode;
}) {
	return (
		<section className="settings-panel">
			<header className="settings-panel-head">
				<h2 className="settings-panel-title">{title}</h2>
				<p className="settings-panel-lede">{lede}</p>
			</header>
			{children}
		</section>
	);
}

function SettingsCard({
	title,
	description,
	children,
	danger,
}: {
	title: ReactNode;
	description?: string;
	children: ReactNode;
	danger?: boolean;
}) {
	return (
		<div
			className={cn("settings-card", danger && "settings-card--danger")}
		>
			<div className="settings-card-head">
				<h3 className="settings-card-title">{title}</h3>
				{description && (
					<p className="settings-card-desc">{description}</p>
				)}
			</div>
			<div className="settings-card-body">{children}</div>
		</div>
	);
}

function SaveRow({
	saved,
	disabled,
}: {
	saved: boolean;
	disabled?: boolean;
}) {
	return (
		<div className="settings-actions">
			<Button type="submit" disabled={disabled}>
				Save changes
			</Button>
			{saved && <span className="settings-saved">Saved</span>}
		</div>
	);
}

function AccountPanel({
	themePref,
	onSave,
	onCycleTheme,
}: {
	session: OnboardingSession;
	themePref: ThemePreference;
	onSave: (patch: {
		firstName: string;
		lastName: string;
		email: string;
	}) => void;
	onCycleTheme: () => void;
}) {
	const { user, isLoaded } = useUser();
	const { openUserProfile } = useClerk();
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [saved, setSaved] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!user) return;
		setFirstName(user.firstName ?? "");
		setLastName(user.lastName ?? "");
	}, [user]);

	const email =
		user?.primaryEmailAddress?.emailAddress ??
		user?.emailAddresses[0]?.emailAddress ??
		"";
	const initial = (
		firstName ||
		user?.fullName ||
		email ||
		"Y"
	)
		.slice(0, 1)
		.toUpperCase();
	const linked = user?.externalAccounts ?? [];
	const identityHint = [
		user?.passwordEnabled ? "Password" : null,
		...linked.map((a) => a.provider),
	]
		.filter((v): v is string => Boolean(v))
		.join(" · ");

	return (
		<PanelShell
			title="Account"
			lede="Your identity in Matrix — agents and approval cards show this name."
		>
			<SettingsCard title="Profile">
				{!isLoaded || !user ? (
					<p className="settings-inline-hint">Loading profile…</p>
				) : (
					<form
						className="settings-form"
						onSubmit={(event) => {
							event.preventDefault();
							void (async () => {
								setSaving(true);
								setError(null);
								try {
									await user.update({
										firstName: firstName.trim() || null,
										lastName: lastName.trim() || null,
									});
									onSave({
										firstName: firstName.trim(),
										lastName: lastName.trim(),
										email,
									});
									setSaved(true);
									window.setTimeout(() => setSaved(false), 1600);
								} catch (err) {
									const message = clerkErrorMessage(err);
									if (message) setError(message);
								} finally {
									setSaving(false);
								}
							})();
						}}
					>
						<div className="settings-profile-row">
							{user.imageUrl ? (
								<img
									className="settings-avatar-lg settings-avatar-lg--img"
									src={user.imageUrl}
									alt=""
									width={48}
									height={48}
								/>
							) : (
								<div className="settings-avatar-lg" aria-hidden>
									{initial}
								</div>
							)}
							<div className="settings-profile-meta">
								<p className="m-0 inline-flex items-center gap-2 text-sm font-semibold">
									{user.fullName ||
										`${firstName} ${lastName}`.trim() ||
										"Your name"}
									<PlanBadge />
								</p>
								<p className="m-0 text-xs text-mx-tertiary">
									{email || "No email on this account"}
								</p>
							</div>
						</div>
						<div className="settings-grid-2">
							<label className="settings-field">
								<span>First name</span>
								<Input
									value={firstName}
									onChange={(e) => setFirstName(e.target.value)}
									autoComplete="given-name"
								/>
							</label>
							<label className="settings-field">
								<span>Last name</span>
								<Input
									value={lastName}
									onChange={(e) => setLastName(e.target.value)}
									autoComplete="family-name"
								/>
							</label>
						</div>
						<label className="settings-field">
							<span>Work email</span>
							<Input type="email" value={email} readOnly disabled />
							<span className="settings-field-hint">
								Change email addresses in your account settings.
							</span>
						</label>
						{error && (
							<p className="settings-form-error" role="alert">
								{error}
							</p>
						)}
						<SaveRow saved={saved} disabled={saving} />
					</form>
				)}
			</SettingsCard>

			<SettingsCard
				title="Appearance"
				description="Matrix follows your preference across the desk."
			>
				<div className="settings-inline-row">
					<div>
						<p className="settings-inline-label">Theme</p>
						<p className="settings-inline-hint">
							Current: {themePref}
						</p>
					</div>
					<Button type="button" variant="outline" onClick={onCycleTheme}>
						Cycle theme
					</Button>
				</div>
			</SettingsCard>

			<SettingsCard
				title="Sign-in"
				description="Password, SSO, and linked sign-in methods."
			>
				<div className="settings-inline-row">
					<div>
						<p className="settings-inline-label">Password & SSO</p>
						<p className="settings-inline-hint">
							{identityHint || "Open account settings to add a sign-in method"}
						</p>
					</div>
					<Button
						type="button"
						variant="outline"
						onClick={() => openUserProfile()}
					>
						Manage account
					</Button>
				</div>
			</SettingsCard>
		</PanelShell>
	);
}

function WorkspacePanel({
	session,
	risk,
	onSave,
	onSaveRisk,
}: {
	session: OnboardingSession;
	risk: RiskConfig;
	onSave: (patch: { company: string; website: string }) => void;
	onSaveRisk: (risk: RiskConfig) => void;
}) {
	const { setActive } = useOrganizationList();
	const { isLoaded, organization, membership } = useOrganization();
	const { tenantName, setTenantName } = usePlatformSession();
	const native = !organization;
	const canManage = native || membership?.role === "org:admin";

	const [name, setName] = useState("");
	const [slug, setSlug] = useState("");
	const [website, setWebsite] = useState(session.profile.website ?? "");
	const [floor, setFloor] = useState(risk.approvalFloor);
	const [cap, setCap] = useState(String(risk.dailySpendCapUsd));
	const [stepUp, setStepUp] = useState(risk.requireStepUpForTier3);
	const [saved, setSaved] = useState(false);
	const [riskSaved, setRiskSaved] = useState(false);
	const [saving, setSaving] = useState(false);
	const [logoBusy, setLogoBusy] = useState(false);
	const [dangerBusy, setDangerBusy] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [confirmDelete, setConfirmDelete] = useState("");
	const logoInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (organization) {
			setName(organization.name);
			setSlug(organization.slug ?? "");
			return;
		}
		if (tenantName) setName(tenantName);
	}, [organization?.id, organization?.name, organization?.slug, tenantName]);

	useEffect(() => {
		setWebsite(session.profile.website ?? "");
	}, [session.profile.website]);

	const saveOrganization = async () => {
		const trimmedName = name.trim();
		if (trimmedName.length < 2) return;
		setSaving(true);
		setError(null);
		try {
			if (organization && canManage) {
				const trimmedSlug = slug.trim().toLowerCase();
				await organization.update({
					name: trimmedName,
					...(trimmedSlug ? { slug: trimmedSlug } : {}),
				});
			} else if (native) {
				const result = await patchWorkspaceName(trimmedName);
				if (!result.ok) {
					setError("Could not save workspace name.");
					return;
				}
				setTenantName(result.name);
			}
			onSave({
				company: trimmedName,
				website: website.trim(),
			});
			setSaved(true);
			window.setTimeout(() => setSaved(false), 1600);
		} catch (err) {
			const message = clerkErrorMessage(err);
			if (message) setError(message);
		} finally {
			setSaving(false);
		}
	};

	const onLogoChange = async (file: File | null) => {
		if (!organization || !canManage) return;
		setLogoBusy(true);
		setError(null);
		try {
			await organization.setLogo({ file });
		} catch (err) {
			const message = clerkErrorMessage(err);
			if (message) setError(message);
		} finally {
			setLogoBusy(false);
		}
	};

	const leaveOrganization = async () => {
		if (!membership || !organization) return;
		setDangerBusy("leave");
		setError(null);
		try {
			await membership.destroy();
			await setActive?.({ organization: null });
		} catch (err) {
			const message = clerkErrorMessage(err);
			if (message) setError(message);
		} finally {
			setDangerBusy(null);
		}
	};

	const deleteOrganization = async () => {
		if (!organization || !canManage) return;
		if (confirmDelete.trim() !== organization.name) return;
		setDangerBusy("delete");
		setError(null);
		try {
			await organization.destroy();
			await setActive?.({ organization: null });
		} catch (err) {
			const message = clerkErrorMessage(err);
			if (message) setError(message);
		} finally {
			setDangerBusy(null);
		}
	};

	return (
		<PanelShell
			title="Workspace"
			lede="Workspace profile and approval thresholds."
		>
			{!isLoaded ? (
				<p className="settings-inline-hint">Loading workspace…</p>
			) : (
				<>
					<SettingsCard
						title="Workspace"
						description={
							native
								? "Workspace name is set during Jules onboarding. You can rename it here."
								: canManage
									? "Name, slug, and logo for this organization."
									: "Only organization admins can edit these fields."
						}
					>
						<form
							className="settings-form"
							onSubmit={(event) => {
								event.preventDefault();
								void saveOrganization();
							}}
						>
							<div className="settings-profile-row">
								{organization?.imageUrl ? (
									<img
										className="settings-avatar-lg settings-avatar-lg--img"
										src={organization.imageUrl}
										alt=""
										width={48}
										height={48}
									/>
								) : (
									<div className="settings-avatar-lg" aria-hidden>
										{(name.trim()[0] ?? "?").toUpperCase()}
									</div>
								)}
								<div className="settings-profile-meta">
									<p className="m-0 text-sm font-semibold">
										{name.trim() || organization?.name || "Workspace"}
									</p>
									<p className="m-0 text-xs text-mx-tertiary">
										{organization
											? `${organization.membersCount} member${organization.membersCount === 1 ? "" : "s"}${organization.slug ? ` · ${organization.slug}` : ""}`
											: "Named by Jules from your company pack"}
									</p>
									{canManage && organization ? (
										<div className="mt-2 flex flex-wrap gap-2">
											<input
												ref={logoInputRef}
												type="file"
												accept="image/*"
												className="sr-only"
												disabled={logoBusy}
												onChange={(e) => {
													const file = e.target.files?.[0] ?? null;
													if (file) void onLogoChange(file);
													e.target.value = "";
												}}
											/>
											<Button
												type="button"
												size="sm"
												variant="outline"
												disabled={logoBusy}
												onClick={() => logoInputRef.current?.click()}
											>
												{logoBusy ? "Uploading…" : "Change logo"}
											</Button>
											{organization.hasImage && (
												<Button
													type="button"
													size="sm"
													variant="ghost"
													disabled={logoBusy}
													onClick={() => void onLogoChange(null)}
												>
													Remove
												</Button>
											)}
										</div>
									) : null}
								</div>
							</div>

							<label className="settings-field">
								<span>Workspace name</span>
								<Input
									value={name}
									onChange={(e) => setName(e.target.value)}
									disabled={!canManage || saving}
								/>
							</label>
							{organization ? (
							<label className="settings-field">
								<span>Slug</span>
								<Input
									value={slug}
									onChange={(e) => setSlug(e.target.value)}
									disabled={!canManage || saving}
									placeholder="acme-co"
									autoComplete="off"
								/>
								<span className="settings-field-hint">
									URL-friendly id for this organization.
								</span>
							</label>
							) : null}
							<label className="settings-field">
								<span>Website</span>
								<Input
									value={website}
									onChange={(e) => setWebsite(e.target.value)}
									placeholder="https://"
									disabled={saving}
								/>
								<span className="settings-field-hint">
									Saved with your workspace profile.
								</span>
							</label>
							{error && (
								<p className="settings-form-error" role="alert">
									{error}
								</p>
							)}
							{!canManage && (
								<p className="settings-inline-hint m-0">
									Ask an admin to change name, slug, or logo. You can
									still update the website field.
								</p>
							)}
							<SaveRow saved={saved} disabled={saving} />
						</form>
					</SettingsCard>

					<SettingsCard
						title="Risk & approvals"
						description="When a request times out without a decision, it is denied."
					>
						<form
							className="settings-form"
							onSubmit={(event) => {
								event.preventDefault();
								const parsed = Number(cap);
								onSaveRisk({
									approvalFloor: floor,
									dailySpendCapUsd: Number.isFinite(parsed)
										? Math.max(0, parsed)
										: risk.dailySpendCapUsd,
									requireStepUpForTier3: stepUp,
								});
								setRiskSaved(true);
								window.setTimeout(() => setRiskSaved(false), 1600);
							}}
						>
							<label className="settings-field">
								<span>Approval floor</span>
								<Select
									value={floor}
									onValueChange={(v) => {
										if (
											v === "tier-1" ||
											v === "tier-2" ||
											v === "tier-3"
										) {
											setFloor(v);
										}
									}}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="tier-1">
											Digest — batch for review
										</SelectItem>
										<SelectItem value="tier-2">
											Each action — recommended
										</SelectItem>
										<SelectItem value="tier-3">
											High-risk only — extra confirm
										</SelectItem>
									</SelectContent>
								</Select>
							</label>
							<label className="settings-field">
								<span>Daily spend cap (USD)</span>
								<Input
									type="number"
									min={0}
									value={cap}
									onChange={(e) => setCap(e.target.value)}
								/>
							</label>
							<label className="settings-check">
								<input
									type="checkbox"
									checked={stepUp}
									onChange={(e) => setStepUp(e.target.checked)}
								/>
								<span>
									Require dashboard re-login for high-risk actions
									(delete, billing, over-cap)
								</span>
							</label>
							<SaveRow saved={riskSaved} />
						</form>
					</SettingsCard>

					{organization ? (
					<SettingsCard
						title="Danger zone"
						description="Leaving removes your access. Deleting removes the organization for everyone."
						danger
					>
						<div className="settings-inline-row">
							<div>
								<p className="settings-inline-label">Leave organization</p>
								<p className="settings-inline-hint">
									You’ll need a new invite to rejoin {organization.name}.
								</p>
							</div>
							<Button
								type="button"
								variant="outline"
								disabled={dangerBusy !== null}
								onClick={() => void leaveOrganization()}
							>
								{dangerBusy === "leave" ? "Leaving…" : "Leave"}
							</Button>
						</div>
						{canManage && (
							<div className="settings-form mt-4 border-t border-mx-sep pt-4">
								<p className="settings-inline-label">Delete organization</p>
								<p className="settings-inline-hint">
									Deletes memberships and invitations. Type{" "}
									<span className="font-semibold text-mx-label">
										{organization.name}
									</span>{" "}
									to confirm.
								</p>
								<label className="settings-field mt-2">
									<span className="sr-only">Confirm name</span>
									<Input
										value={confirmDelete}
										onChange={(e) => setConfirmDelete(e.target.value)}
										placeholder={organization.name}
										autoComplete="off"
										disabled={dangerBusy !== null}
									/>
								</label>
								<Button
									type="button"
									variant="destructive"
									className="mt-2"
									disabled={
										dangerBusy !== null ||
										confirmDelete.trim() !== organization.name
									}
									onClick={() => void deleteOrganization()}
								>
									{dangerBusy === "delete" ? "Deleting…" : "Delete"}
								</Button>
							</div>
						)}
					</SettingsCard>
					) : null}
				</>
			)}
		</PanelShell>
	);
}

function MembersPanel() {
	const { user } = useUser();
	const {
		isLoaded,
		organization,
		membership,
		memberships,
		invitations,
	} = useOrganization({
		memberships: { infinite: true },
		invitations: { infinite: true, status: ["pending"] },
	});

	const [email, setEmail] = useState("");
	const [role, setRole] = useState<InviteRole>("viewer");
	const [busy, setBusy] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [sent, setSent] = useState(false);

	const canManage = membership?.role === "org:admin";

	const people = useMemo((): WorkspaceMember[] => {
		const active: WorkspaceMember[] = (memberships?.data ?? []).map((m) => {
			const pud = m.publicUserData;
			const name =
				[pud?.firstName, pud?.lastName].filter(Boolean).join(" ").trim() ||
				pud?.identifier ||
				"Member";
			return {
				id: m.id,
				name,
				email: pud?.identifier ?? "",
				role: clerkOrgRoleToMemberRole(m.role),
				status: "active" as const,
			};
		});
		const pending: WorkspaceMember[] = (invitations?.data ?? []).map((inv) => ({
			id: inv.id,
			name: inv.emailAddress.split("@")[0] ?? inv.emailAddress,
			email: inv.emailAddress,
			role: clerkOrgRoleToMemberRole(inv.role),
			status: "invited" as const,
		}));
		return [...active, ...pending];
	}, [memberships?.data, invitations?.data]);

	const refreshLists = async () => {
		await Promise.all([
			memberships?.revalidate?.(),
			invitations?.revalidate?.(),
		]);
	};

	const run = async (key: string, action: () => Promise<void>) => {
		setBusy(key);
		setError(null);
		try {
			await action();
			await refreshLists();
		} catch (err) {
			const message = clerkErrorMessage(err);
			if (message) setError(message);
		} finally {
			setBusy(null);
		}
	};

	const sendInvite = async () => {
		const trimmed = email.trim().toLowerCase();
		if (!organization || !trimmed.includes("@")) return;
		await run("invite", async () => {
			await organization.inviteMember({
				emailAddress: trimmed,
				role: memberRoleToClerkOrgRole(role),
			});
			setEmail("");
			setSent(true);
			window.setTimeout(() => setSent(false), 2000);
		});
	};

	const changeRole = async (memberId: string, next: InviteRole) => {
		if (!organization) return;
		const row = memberships?.data?.find((m) => m.id === memberId);
		const userId = row?.publicUserData?.userId;
		if (!userId) return;
		await run(`role-${memberId}`, async () => {
			await organization.updateMember({
				userId,
				role: memberRoleToClerkOrgRole(next),
			});
		});
	};

	const removePerson = async (member: WorkspaceMember) => {
		if (!organization) return;
		if (member.status === "invited") {
			const inv = invitations?.data?.find((i) => i.id === member.id);
			if (!inv) return;
			await run(`rm-${member.id}`, async () => {
				await inv.revoke();
			});
			return;
		}
		const row = memberships?.data?.find((m) => m.id === member.id);
		const userId = row?.publicUserData?.userId;
		if (!userId || userId === user?.id) return;
		await run(`rm-${member.id}`, async () => {
			await organization.removeMember(userId);
		});
	};

	return (
		<PanelShell
			title="Members"
			lede="Invite people to this workspace. Seats are free — you pay for usage, not headcount."
		>
			{!isLoaded ? (
				<p className="settings-inline-hint">Loading members…</p>
			) : !organization ? (
				<p className="settings-inline-hint">
					Teammate invites come after Jules finishes workspace setup.
					This workspace is yours — not a Clerk organization.
				</p>
			) : (
				<>
					<div className="settings-stat-row">
						<div className="settings-stat">
							<p className="settings-stat-value">
								{memberships?.count ?? people.filter((m) => m.status === "active").length}
							</p>
							<p className="settings-stat-label">People</p>
						</div>
						<div className="settings-stat">
							<p className="settings-stat-value">
								{invitations?.count ??
									people.filter((m) => m.status === "invited").length}
							</p>
							<p className="settings-stat-label">Pending invites</p>
						</div>
						<div className="settings-stat">
							<p className="settings-stat-value">
								{
									people.filter(
										(m) =>
											m.status === "active" && m.role === "admin",
									).length
								}
							</p>
							<p className="settings-stat-label">Admins</p>
						</div>
					</div>

					{error && (
						<p className="settings-form-error" role="alert">
							{error}
						</p>
					)}

					<SettingsCard
						title="Invite"
						description="We’ll email a join link. Member is the safer default; Admin can manage people and settings."
					>
						{!canManage ? (
							<p className="settings-inline-hint">
								Only organization admins can send invites.
							</p>
						) : (
							<form
								className="settings-invite"
								onSubmit={(event) => {
									event.preventDefault();
									void sendInvite();
								}}
							>
								<Input
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="colleague@company.com"
									aria-label="Invite email"
									className="min-w-0 flex-1"
									disabled={busy === "invite"}
								/>
								<Select
									value={role}
									onValueChange={(v) => {
										if (v === "admin" || v === "viewer") setRole(v);
									}}
									disabled={busy === "invite"}
								>
									<SelectTrigger className="w-[140px]">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="viewer">Member</SelectItem>
										<SelectItem value="admin">Admin</SelectItem>
									</SelectContent>
								</Select>
								<Button
									type="submit"
									disabled={!email.includes("@") || busy === "invite"}
								>
									{busy === "invite"
										? "Sending…"
										: sent
											? "Sent"
											: "Send invite"}
								</Button>
							</form>
						)}
					</SettingsCard>

					<SettingsCard title="People">
						{memberships?.isLoading && people.length === 0 ? (
							<p className="settings-inline-hint">Loading people…</p>
						) : people.length === 0 ? (
							<p className="settings-inline-hint">No members yet.</p>
						) : (
							<ul className="settings-member-list">
								{people.map((member) => {
									const isSelf =
										member.status === "active" &&
										memberships?.data?.find((m) => m.id === member.id)
											?.publicUserData?.userId === user?.id;
									const rowBusy =
										busy === `role-${member.id}` ||
										busy === `rm-${member.id}`;
									return (
										<li key={member.id} className="settings-member">
											<div
												className="settings-member-avatar"
												aria-hidden
											>
												{member.name.slice(0, 1).toUpperCase()}
											</div>
											<div className="settings-member-copy">
												<p className="settings-member-name">
													{member.name}
													{isSelf && (
														<Badge
															variant="outline"
															className="ml-2 text-[10px]"
														>
															You
														</Badge>
													)}
													{member.status === "invited" && (
														<Badge
															variant="outline"
															className="ml-2 text-[10px]"
														>
															Invited
														</Badge>
													)}
												</p>
												<p className="settings-member-email">
													{member.email}
												</p>
												<p className="settings-member-hint">
													{roleHint(member.role)}
												</p>
											</div>
											{member.status === "invited" || !canManage || isSelf ? (
												<Badge
													variant="outline"
													className="text-[10px]"
												>
													{member.role === "admin" ? "Admin" : "Member"}
												</Badge>
											) : (
												<Select
													value={
														member.role === "admin" ? "admin" : "viewer"
													}
													onValueChange={(v) => {
														if (v === "admin" || v === "viewer") {
															void changeRole(member.id, v);
														}
													}}
													disabled={rowBusy}
												>
													<SelectTrigger className="w-[120px]">
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														<SelectItem value="viewer">
															Member
														</SelectItem>
														<SelectItem value="admin">Admin</SelectItem>
													</SelectContent>
												</Select>
											)}
											{canManage && !isSelf && (
												<Button
													type="button"
													variant="ghost"
													size="sm"
													className="text-muted-foreground"
													disabled={rowBusy}
													onClick={() => void removePerson(member)}
												>
													{member.status === "invited"
														? "Revoke"
														: "Remove"}
												</Button>
											)}
										</li>
									);
								})}
							</ul>
						)}
					</SettingsCard>
				</>
			)}
		</PanelShell>
	);
}

function NotificationsPanel({
	prefs,
	onSave,
}: {
	prefs: NotificationPrefs;
	onSave: (prefs: NotificationPrefs) => void;
}) {
	const [local, setLocal] = useState(prefs);
	const [saved, setSaved] = useState(false);

	const toggle = (key: keyof NotificationPrefs) => {
		setLocal((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	const rows: {
		key: keyof NotificationPrefs;
		label: string;
		hint: string;
	}[] = [
		{
			key: "approvalPush",
			label: "Approval cards in channels",
			hint: "Slack / WhatsApp / Teams when an action needs you",
		},
		{
			key: "approvalEmail",
			label: "Approval email",
			hint: "Magic-link confirm — never approve on GET",
		},
		{
			key: "digestDaily",
			label: "Daily digest",
			hint: "Batch lighter items instead of one-by-one",
		},
		{
			key: "agentMentions",
			label: "Agent handoffs",
			hint: "When one agent asks another on the desk",
		},
		{
			key: "billingAlerts",
			label: "Billing alerts",
			hint: "Failed payment, seat limit, spend cap",
		},
	];

	return (
		<PanelShell
			title="Notifications"
			lede="Where your team gets approval requests — keep noise low, never miss spend."
		>
			<SettingsCard title="Preferences">
				<form
					className="settings-form"
					onSubmit={(event) => {
						event.preventDefault();
						onSave(local);
						setSaved(true);
						window.setTimeout(() => setSaved(false), 1600);
					}}
				>
					<ul className="settings-toggle-list">
						{rows.map((row) => (
							<li key={row.key} className="settings-toggle-row">
								<label className="settings-check settings-check--row">
									<input
										type="checkbox"
										checked={local[row.key]}
										onChange={() => toggle(row.key)}
									/>
									<span>
										<span className="settings-inline-label">
											{row.label}
										</span>
										<span className="settings-inline-hint block">
											{row.hint}
										</span>
									</span>
								</label>
							</li>
						))}
					</ul>
					<SaveRow saved={saved} />
				</form>
			</SettingsCard>
		</PanelShell>
	);
}

function SecurityPanel({ risk }: { risk: RiskConfig }) {
	const { user, isLoaded: userLoaded } = useUser();
	const { session: current } = useSession();
	const { isLoaded: sessionsLoaded, sessions } = useSessionList();
	const { openUserProfile } = useClerk();

	const [busy, setBusy] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [signOutOpen, setSignOutOpen] = useState(false);
	const [totpSecret, setTotpSecret] = useState<string | null>(null);
	const [totpUri, setTotpUri] = useState<string | null>(null);
	const [totpCode, setTotpCode] = useState("");
	const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

	const passkeys = user?.passkeys ?? [];
	const externalAccounts = user?.externalAccounts ?? [];
	const twoFactorOn = Boolean(user?.twoFactorEnabled);
	const totpOn = Boolean(user?.totpEnabled);
	const backupOn = Boolean(user?.backupCodeEnabled);

	const run = async (key: string, action: () => Promise<void>) => {
		setBusy(key);
		setError(null);
		try {
			await action();
			await user?.reload();
		} catch (err) {
			const message = clerkErrorMessage(err);
			if (message) setError(message);
		} finally {
			setBusy(null);
		}
	};

	return (
		<PanelShell
			title="Security"
			lede="Passkeys, two-factor, sessions, and step-up for high-risk actions."
		>
			{error && (
				<p className="settings-form-error" role="alert">
					{error}
				</p>
			)}

			<SettingsCard
				title="Password"
				description="Primary password for email sign-in."
			>
				<div className="settings-inline-row">
					<div>
						<p className="settings-inline-label">Password</p>
						<p className="settings-inline-hint">
							{!userLoaded
								? "Loading…"
								: user?.passwordEnabled
									? "Set on this account"
									: "Not set — add one or use Google / a passkey"}
						</p>
					</div>
					<Button
						type="button"
						variant="outline"
						onClick={() => openUserProfile()}
					>
						{user?.passwordEnabled ? "Update password" : "Set password"}
					</Button>
				</div>
			</SettingsCard>

			<SettingsCard
				title="Passkeys"
				description="Sign in with Face ID, Touch ID, Windows Hello, or a hardware key."
			>
				{passkeys.length === 0 ? (
					<p className="settings-inline-hint m-0">No passkeys yet.</p>
				) : (
					<ul className="settings-session-list">
						{passkeys.map((pk) => (
							<li key={pk.id} className="settings-session">
								<div>
									<p className="settings-inline-label">
										{pk.name?.trim() || "Passkey"}
									</p>
									<p className="settings-inline-hint">
										Added {pk.createdAt.toLocaleDateString()}
										{pk.lastUsedAt
											? ` · Last used ${pk.lastUsedAt.toLocaleDateString()}`
											: ""}
									</p>
								</div>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									disabled={busy === `pk-del-${pk.id}`}
									onClick={() => {
										void run(`pk-del-${pk.id}`, async () => {
											await pk.delete();
										});
									}}
								>
									Remove
								</Button>
							</li>
						))}
					</ul>
				)}
				<div className="settings-inline-row mt-1">
					<p className="settings-inline-hint m-0">
						Works on this device’s platform authenticator.
					</p>
					<Button
						type="button"
						disabled={busy === "passkey" || !user}
						onClick={() => {
							void run("passkey", async () => {
								if (!user) return;
								await user.createPasskey();
							});
						}}
					>
						{busy === "passkey" ? "Waiting…" : "Create passkey"}
					</Button>
				</div>
			</SettingsCard>

			<SettingsCard
				title="Two-factor authentication"
				description="Authenticator app (TOTP) plus optional backup codes."
			>
				<div className="settings-toggle-list">
					<div className="settings-inline-row">
						<div>
							<p className="settings-inline-label">Authenticator app</p>
							<p className="settings-inline-hint">
								{totpOn ? "Enabled" : "Not set up"}
							</p>
						</div>
						{totpOn ? (
							<Button
								type="button"
								variant="outline"
								disabled={busy === "totp-off"}
								onClick={() => {
									void run("totp-off", async () => {
										if (!user) return;
										await user.disableTOTP();
										setTotpSecret(null);
										setTotpUri(null);
										setTotpCode("");
									});
								}}
							>
								Disable
							</Button>
						) : (
							<Button
								type="button"
								disabled={busy === "totp-start" || !user || Boolean(totpSecret)}
								onClick={() => {
									void run("totp-start", async () => {
										if (!user) return;
										const totp = await user.createTOTP();
										setTotpSecret(totp.secret ?? null);
										setTotpUri(totp.uri ?? null);
									});
								}}
							>
								Set up
							</Button>
						)}
					</div>

					{totpSecret && !totpOn && (
						<div className="settings-totp-setup">
							<p className="settings-inline-hint m-0">
								Add this secret in your authenticator app, then enter a
								6-digit code.
							</p>
							{totpUri && (
								<img
									className="settings-totp-qr"
									src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(totpUri)}`}
									alt="Authenticator QR code"
									width={160}
									height={160}
								/>
							)}
							<code className="settings-totp-secret">{totpSecret}</code>
							<div className="settings-inline-row">
								<Input
									inputMode="numeric"
									autoComplete="one-time-code"
									placeholder="123456"
									value={totpCode}
									onChange={(e) => setTotpCode(e.target.value.trim())}
									maxLength={6}
								/>
								<Button
									type="button"
									disabled={
										busy === "totp-verify" || totpCode.length < 6
									}
									onClick={() => {
										void run("totp-verify", async () => {
											if (!user) return;
											await user.verifyTOTP({ code: totpCode });
											setTotpSecret(null);
											setTotpUri(null);
											setTotpCode("");
										});
									}}
								>
									Verify
								</Button>
							</div>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								onClick={() => {
									setTotpSecret(null);
									setTotpUri(null);
									setTotpCode("");
								}}
							>
								Cancel
							</Button>
						</div>
					)}

					<div className="settings-inline-row">
						<div>
							<p className="settings-inline-label">Backup codes</p>
							<p className="settings-inline-hint">
								{backupOn
									? "Generated — store them offline"
									: twoFactorOn
										? "Generate codes for account recovery"
										: "Enable authenticator first"}
							</p>
						</div>
						<Button
							type="button"
							variant="outline"
							disabled={busy === "backup" || !user || !twoFactorOn}
							onClick={() => {
								void run("backup", async () => {
									if (!user) return;
									const codes = await user.createBackupCode();
									setBackupCodes(codes.codes ?? null);
								});
							}}
						>
							{backupOn ? "Regenerate" : "Generate"}
						</Button>
					</div>

					{backupCodes && backupCodes.length > 0 && (
						<div className="settings-backup-codes">
							<p className="settings-inline-hint m-0">
								Copy these now — they won’t be shown again.
							</p>
							<ul>
								{backupCodes.map((code) => (
									<li key={code}>
										<code>{code}</code>
									</li>
								))}
							</ul>
						</div>
					)}
				</div>
			</SettingsCard>

			<SettingsCard
				title="Connected accounts"
				description="Accounts linked for sign-in."
			>
				{externalAccounts.length === 0 ? (
					<p className="settings-inline-hint m-0">
						No connected accounts. Link Google or others in account settings.
					</p>
				) : (
					<ul className="settings-session-list">
						{externalAccounts.map((account) => (
							<li key={account.id} className="settings-session">
								<div>
									<p className="settings-inline-label">
										{account.provider}
									</p>
									<p className="settings-inline-hint">
										{account.emailAddress ?? account.username ?? account.id}
									</p>
								</div>
								<Badge variant="outline" className="text-[10px]">
									Linked
								</Badge>
							</li>
						))}
					</ul>
				)}
				<div className="settings-actions">
					<Button
						type="button"
						variant="outline"
						onClick={() => openUserProfile()}
					>
						Manage connected accounts
					</Button>
				</div>
			</SettingsCard>

			<SettingsCard
				title="Active sessions"
				description="End a session to sign that device out immediately."
			>
				{!sessionsLoaded ? (
					<p className="settings-inline-hint">Loading sessions…</p>
				) : (
					<ul className="settings-session-list">
						{(sessions ?? []).map((s) => {
							const isCurrent = s.id === current?.id;
							const activity =
								"latestActivity" in s
									? (
											s as {
												latestActivity?: {
													browserName?: string;
													deviceType?: string;
													city?: string;
													country?: string;
												};
											}
										).latestActivity
									: undefined;
							const where = [
								activity?.browserName,
								activity?.deviceType,
								activity?.city,
								activity?.country,
							]
								.filter(Boolean)
								.join(" · ");
							const last =
								s.lastActiveAt instanceof Date
									? s.lastActiveAt.toLocaleString()
									: "Unknown";
							return (
								<li key={s.id} className="settings-session">
									<div>
										<p className="settings-inline-label">
											{isCurrent
												? "This browser"
												: where || "Other session"}
										</p>
										<p className="settings-inline-hint">
											Last active {last}
										</p>
									</div>
									{isCurrent ? (
										<Badge className="text-[10px]">Current</Badge>
									) : (
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={() => {
												void s.end();
											}}
										>
											End
										</Button>
									)}
								</li>
							);
						})}
					</ul>
				)}
				<div className="settings-inline-row mt-2">
					<p className="settings-inline-hint m-0">
						Sign out of this device only.
					</p>
					<Button
						type="button"
						variant="outline"
						onClick={() => setSignOutOpen(true)}
					>
						Sign out
					</Button>
				</div>
				<SignOutConfirm
					open={signOutOpen}
					onOpenChange={setSignOutOpen}
				/>
			</SettingsCard>

			<SettingsCard
				title="Extra confirmation"
				description="High-risk actions (delete, billing, over-cap) require signing in again in the dashboard — channel buttons alone are not enough."
			>
				<div className="settings-inline-row">
					<div>
						<p className="settings-inline-label">
							Required for high-risk actions
						</p>
						<p className="settings-inline-hint">
							{risk.requireStepUpForTier3
								? "On — configured in Workspace → Risk"
								: "Off — turn on under Workspace → Risk"}
						</p>
					</div>
					<Badge variant="outline" className="text-[10px]">
						{risk.requireStepUpForTier3 ? "Enabled" : "Disabled"}
					</Badge>
				</div>
			</SettingsCard>
		</PanelShell>
	);
}

function BillingPanel() {
	const { tenantName } = usePlatformSession();
	const { pro } = useWorkingCopyPlan();
	const resolved = resolveTheme(readThemePreference());
	const workspace = tenantName?.trim() || "this workspace";

	return (
		<PanelShell
			title="Billing"
			lede="Pay as you go or a monthly plan. Either one unlocks a working copy."
		>
			<SettingsCard
				title={
					<span className="inline-flex items-center gap-2">
						{pro ? "Pro" : "Choose a plan"}
						<PlanBadge />
					</span>
				}
				description={
					pro
						? "This workspace can start a working copy. Switch plans anytime."
						: "Pick a plan to start. Checkout stays in this window."
				}
			>
				<div className="settings-billing-plans">
					<ClerkOrgPlans
						open
						workspaceName={workspace}
						resolved={resolved}
					/>
				</div>
			</SettingsCard>
		</PanelShell>
	);
}

